import { useMemo } from "react";
import useDocumentDetails from "../../pages/documents/components/DocumentItem/hooks/useDocumentDetails";
import useEventDetails from "../../pages/events/pages/event/hooks/useEventDetails";
import useProjectDetails from "../../pages/projects/pages/project/hooks/useProjectDetails";
import useOrganizationSharings from "../useOrganizationSharings";
import { PublicUserOrganization } from "../../__generated__/graphql";
import useOrganizationMembers from "../useOrganizationMembers";
import useActivityById from "../../pages/projects/pages/project/components/Schedule/hooks/useActivityById";

export type ReasonForSharing =
  | "event"
  | "event_document"
  | "project"
  | "project_document"
  | "activity_document"
  | "document";

interface UseManageParticipantsProps {
  eventId?: string | null;
  documentId?: string | null;
  projectId?: string | null;
  activityId?: string | null;
  participantsFor: ReasonForSharing;
}

export default function useManageParticipants({
  eventId,
  documentId,
  projectId,
  activityId,
  participantsFor,
}: UseManageParticipantsProps) {
  const {
    data: eventData,
    loading: isEventLoading,
    error: eventError,
  } = useEventDetails(eventId);
  const {
    data: documentData,
    loading: isDocumentLoading,
    error: documentError,
  } = useDocumentDetails(documentId);
  const {
    data: projectData,
    loading: isProjectLoading,
    error: projectError,
  } = useProjectDetails(projectId);
  const {
    data: activityData,
    loading: isActivityLoading,
    error: activityError,
  } = useActivityById(activityId);
  const {
    externalUserOrganizations,
    loading: isOrganizationSharingsLoading,
    error: organizationSharingsError,
  } = useOrganizationSharings();
  const {
    data: organizationMembersData,
    loading: isOrganizationMembersLoading,
    error: organizationMembersError,
  } = useOrganizationMembers();

  const loading =
    isEventLoading ||
    isDocumentLoading ||
    isProjectLoading ||
    isActivityLoading ||
    isOrganizationSharingsLoading ||
    isOrganizationMembersLoading;
  const error =
    eventError ||
    documentError ||
    projectError ||
    activityError ||
    organizationSharingsError ||
    organizationMembersError ||
    organizationMembersError;

  if (participantsFor === "event" && !eventId) {
    throw new Error("Event ID is required");
  }

  if (
    (participantsFor === "event_document" ||
      participantsFor === "project_document") &&
    !documentId
  ) {
    throw new Error("Document ID is required");
  }

  if (participantsFor === "project" && !projectId) {
    throw new Error("Project ID is required");
  }

  if (participantsFor === "activity_document" && !activityId) {
    throw new Error("Activity ID is required");
  }

  const organizationMembers: PublicUserOrganization[] = useMemo(() => {
    return (
      organizationMembersData?.organizationMembers?.filter(
        (member) => member !== undefined && member !== null
      ) ?? []
    ).map((member) => ({
      id: member.id,
      name: member.name,
      organizationName: member.organizationName,
      organizationId: member.organizationId,
      organizationType: member.organizationType,
      email: member.email,
      phoneNumber: member.phoneNumber,
      avatarBucketKey: member.avatarBucketKey,
      jobTitle: member.jobTitle,
    }));
  }, [organizationMembersData]);

  const possibleParticipants = useMemo(() => {
    let participants: PublicUserOrganization[] = [];

    const formattedEventParticipants =
      eventData?.event?.eventParticipants
        ?.map((participant) => participant?.publicUserOrganization)
        ?.filter((participant) => participant !== undefined) ?? [];

    const formattedProjectParticipants =
      projectData?.project?.projectParticipants
        ?.map((participant) => participant?.publicUserOrganization)
        ?.filter((participant) => participant !== undefined) ?? [];

    const formattedExternalUserOrganizations = (
      externalUserOrganizations
        ?.map((organization) => organization)
        ?.filter((organization) => organization !== undefined) ?? []
    ).map((userOrganization) => ({
      // Map to PublicUserOrganization
      id: userOrganization.id,
      name: userOrganization.name,
      organizationName: userOrganization.organizationName,
      organizationId: userOrganization.organizationId,
      organizationType: userOrganization.organizationType,
      email: userOrganization.email,
      phoneNumber: userOrganization.phoneNumber,
      avatarBucketKey: userOrganization.avatarBucketKey,
      jobTitle: userOrganization.jobTitle,
    }));

    const formattedActivityParticipants =
      activityData?.activity?.activityParticipants
        ?.map((participant) => participant?.publicUserOrganization)
        ?.filter((participant) => participant !== undefined) ?? [];

    if (participantsFor === "event") {
      participants = [
        ...formattedEventParticipants,
        ...formattedProjectParticipants,
        ...formattedExternalUserOrganizations,
      ];
    }

    if (participantsFor === "event_document") {
      participants = [...formattedEventParticipants];
    }

    if (participantsFor === "project_document") {
      participants = [...formattedProjectParticipants];
    }

    if (participantsFor === "project") {
      participants = [
        ...formattedExternalUserOrganizations,
        ...formattedProjectParticipants,
      ];
    }

    if (participantsFor === "document") {
      participants = [...formattedExternalUserOrganizations];
    }

    if (participantsFor === "activity_document") {
      participants = [...formattedActivityParticipants];
    }

    // Remove duplicates and organization members from this list
    return participants
      .filter(
        (participant, index, self) =>
          index === self.findIndex((t) => t.id === participant.id)
      )
      .filter(
        (participant) =>
          !organizationMembers.some((member) => member.id === participant.id)
      );
  }, [
    eventData,
    projectData,
    externalUserOrganizations,
    activityData,
    participantsFor,
    organizationMembers,
  ]);

  const alreadyAddedParticipants = useMemo(() => {
    let participants: PublicUserOrganization[] = [];

    if (participantsFor === "event") {
      participants =
        eventData?.event?.eventParticipants
          ?.map((participant) => participant?.publicUserOrganization)
          ?.filter((participant) => participant !== undefined) ?? [];
    }

    if (participantsFor === "project") {
      participants =
        projectData?.project?.projectParticipants
          ?.map((participant) => participant?.publicUserOrganization)
          ?.filter((participant) => participant !== undefined) ?? [];
    }

    if (
      participantsFor === "event_document" ||
      participantsFor === "project_document" ||
      participantsFor === "activity_document" ||
      participantsFor === "document"
    ) {
      participants =
        documentData?.document?.documentSharings
          ?.map((sharing) => sharing?.recipientPublicUserOrganization)
          ?.filter((participant) => participant !== undefined) ?? [];
    }

    return participants;
  }, [eventData, documentData, projectData, participantsFor]);

  return {
    error,
    loading,
    possibleParticipants,
    alreadyAddedParticipants,
    organizationMembers,
  };
}
